import React from 'react';
import { useTrackEvent } from 'ha/modules/Analytics/helpers/TrackEvent';
import { useFeatureFlags } from 'ha/modules/FeatureFlags';
import { useServices } from 'ha/services';

import {
  FB_ADS,
  PINTEREST_ADS,
  TIKTOK_ADS,
} from 'ha/modules/Analytics/constants';

import { SearchListing } from 'ha/pages/Search/types';

type ListingId = string | number;
type SearchItem = Pick<SearchListing, 'internalID'>;

interface ConversionTracker {
  trackAddToCart: (listingId: ListingId, roomType: string) => void;
  trackCheckout: (
    listingId: ListingId,
    price: number,
    currency: string,
  ) => void;
  trackSubscribe: (currency: string, value: number) => void;
  trackContentView: (listingId: ListingId, countryCode: string) => void;
  trackSearchView: (listings: SearchItem[], countryCode: string) => void;
  trackSignUp: (email: string) => void;
}

function isGermanContent(countryCode: string) {
  return countryCode?.toLowerCase() === 'de';
}

function useConversionTracker(): ConversionTracker {
  const trackEvent = useTrackEvent();
  const { tiktok } = useServices();
  const { pinterestAds } = useFeatureFlags();

  const trackAddToCart = React.useCallback(
    (listingId: ListingId, roomType: string) => {
      trackEvent(FB_ADS.ADD_TO_CART, {
        content_type: ['home_listing', roomType],
        content_ids: listingId,
      });

      tiktok.track(TIKTOK_ADS.ADD_TO_CART, {
        content_id: listingId,
        content_type: 'product',
      });

      if (pinterestAds === 'on') {
        trackEvent(PINTEREST_ADS.ADD_TO_CART, {
          'Product ID': listingId,
        });
      }
    },
    [pinterestAds, tiktok, trackEvent],
  );

  const trackCheckout = React.useCallback(
    (listingId: ListingId, price: number, currency: string) => {
      trackEvent(FB_ADS.CHECKOUT, {
        content_type: 'home_listing',
        content_ids: [listingId],
        nonInteraction: 1,
      });

      tiktok.track(TIKTOK_ADS.CHECKOUT, {
        content_id: listingId,
        content_type: 'product',
        quantity: 1,
        price,
        currency,
      });

      if (pinterestAds === 'on') {
        trackEvent(PINTEREST_ADS.CHECKOUT, {
          'Product ID': listingId,
          Value: price,
          Currency: currency,
        });
      }
    },
    [pinterestAds, tiktok, trackEvent],
  );

  const trackSubscribe = React.useCallback(
    (currency: string, value: number) => {
      trackEvent(FB_ADS.SUBSCRIBE, {
        content_type: 'home_listing',
        currency,
        value,
        revenue: value,
      });
    },
    [trackEvent],
  );

  const trackContentView = React.useCallback(
    (listingId: ListingId, countryCode: string) => {
      trackEvent(FB_ADS.VIEW, {
        content_type: 'home_listing',
        content_ids: [listingId],
        nonInteraction: 1,
      });

      const tiktokPayload = {
        content_id: listingId,
        content_type: 'product',
      };

      tiktok.track(TIKTOK_ADS.VIEW, tiktokPayload);
      if (isGermanContent(countryCode)) {
        tiktok.track(TIKTOK_ADS.RETARGET_COUNTRY_SPECIFIC, tiktokPayload);
      }
    },
    [tiktok, trackEvent],
  );

  const trackSearchView = React.useCallback(
    (listings: SearchItem[], countryCode: string) => {
      const listingsIds = listings.map(({ internalID }) => internalID);

      trackEvent(FB_ADS.SEARCH, {
        content_type: 'home_listing',
        content_ids: listingsIds,
        nonInteraction: 1,
      });

      const tiktokPayload = {
        contents: listingsIds.map(listingId => ({
          content_id: listingId,
          content_type: 'product',
        })),
      };

      tiktok.track(TIKTOK_ADS.SEARCH, tiktokPayload);
      if (isGermanContent(countryCode)) {
        tiktok.track(TIKTOK_ADS.RETARGET_COUNTRY_SPECIFIC, tiktokPayload);
      }

      if (pinterestAds === 'on') {
        trackEvent(PINTEREST_ADS.SEARCH, {
          line_items: listingsIds.map(listingId => ({
            product_id: listingId,
          })),
        });
      }
    },
    [pinterestAds, tiktok, trackEvent],
  );

  const trackSignUp = React.useCallback(
    (email: string) => {
      if (pinterestAds === 'on') {
        trackEvent(PINTEREST_ADS.SIGN_UP, { email });
      }
    },
    [pinterestAds, trackEvent],
  );

  return {
    trackAddToCart,
    trackCheckout,
    trackSubscribe,
    trackContentView,
    trackSearchView,
    trackSignUp,
  };
}

export default useConversionTracker;
